import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ArrowDown } from "lucide-react";
import { DOCTOR, IMAGES, STATS } from "@/data";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.12 },
  }),
};

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  const titleLines = ["Gynecologic", "cancer care,", "with heart."];

  return (
    <section ref={ref} className="relative pt-32 pb-16 sm:pt-40 sm:pb-24 overflow-hidden" data-testid="hero">
      {/* Parallax abstract wash */}
      <motion.div
        style={{ y: yBg }}
        className="pointer-events-none absolute -top-40 -right-40 w-[70vw] h-[70vw] max-w-[900px] max-h-[900px] rounded-full opacity-[0.14]"
      >
        <img src={IMAGES.heroAbstract} alt="" className="w-full h-full object-cover rounded-full" />
      </motion.div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-6 items-center relative">
        {/* Left — text */}
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="overline text-[#B9735A] mb-6"
          >
            Consultant Gynecologic Oncologist · SPARSH Hospital, Bengaluru
          </motion.p>

          <h1 className="font-serif-display font-light text-[#1A1C1A] text-5xl sm:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight">
            {titleLines.map((t, i) => (
              <span className="reveal-line" key={i}>
                <motion.span
                  className="block"
                  custom={i}
                  variants={line}
                  initial="hidden"
                  animate="show"
                >
                  {i === titleLines.length - 1 ? (
                    <span className="italic text-[#B9735A]">{t}</span>
                  ) : (
                    t
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-xl text-[#4A4D4A] text-base sm:text-lg leading-relaxed"
          >
            Personalized, evidence-based surgical care for women with gynecologic
            cancers — from prevention and early diagnosis to advanced minimally
            invasive surgery, survivorship and quality of life.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={onBook}
              className="inline-flex items-center gap-2 rounded-full bg-[#B9735A] text-[#FDFBF7] font-medium px-7 py-3.5 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#B9735A]/30 transition-[transform,box-shadow] duration-300"
              data-testid="hero-book"
            >
              Book a Consultation
            </button>
            <a
              href={`tel:+${DOCTOR.phoneIntl}`}
              className="inline-flex items-center gap-2 rounded-full border border-[#1A1C1A]/15 text-[#1A1C1A] font-medium px-6 py-3.5 hover:bg-[#1A1C1A] hover:text-[#FDFBF7] transition-colors duration-300"
              data-testid="hero-call"
            >
              <Phone size={16} /> Call {DOCTOR.phone}
            </a>
          </motion.div>
        </div>

        {/* Right — portrait */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto max-w-sm"
          >
            <div className="portrait-arch bg-[#F6F4EE] shadow-2xl shadow-black/10 border border-black/5">
              <motion.img
                src={DOCTOR.photo}
                alt={DOCTOR.name}
                style={{ y: yImg, scale: scaleImg }}
                className="w-full h-[440px] sm:h-[520px] object-cover object-top"
                data-testid="hero-portrait"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.7 }}
              className="absolute -left-4 sm:-left-10 bottom-8 bg-[#FDFBF7] rounded-2xl shadow-xl border border-black/5 px-5 py-4"
            >
              <p className="font-serif-display text-3xl text-[#B9735A] leading-none">12+</p>
              <p className="text-xs text-[#4A4D4A] mt-1 max-w-[110px]">Years of clinical experience</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Stats strip */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="max-w-7xl mx-auto px-5 sm:px-8 mt-16 sm:mt-24"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/5 rounded-3xl overflow-hidden border border-black/5">
          {STATS.map((s) => (
            <div key={s.label} className="bg-[#FDFBF7] px-6 py-8 text-center hover:bg-[#F6F4EE] transition-colors duration-300">
              <p className="font-serif-display text-4xl sm:text-5xl text-[#1A1C1A]">{s.value}</p>
              <p className="text-xs sm:text-sm text-[#4A4D4A] mt-2">{s.label}</p>
            </div>
          ))}
        </div>
      </motion.div>

      <div className="flex justify-center mt-14 text-[#B9735A]">
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={20} />
        </motion.div>
      </div>
    </section>
  );
};
